import React, { FC, useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import { gqlSystemRoleCode as SYSTEM_ROLE } from '@minecraft.graphql-types';
import Loader from '@minecraft.loader';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { CreateCouponRedemptionDoc } from '@minecraft.graphql-operations';
import { QUERY_PARAMS } from '../../constants';
import { getAccessToken, parseJwt } from '../../utils';

export const ApplyCoupon: FC = ({ children }) => {
  const { t } = useTranslation();
  const [couponCode] = useState(localStorage.getItem(QUERY_PARAMS.couponCode));
  const beCodeEnabled = useFeatureExperiment(FEATURE_FLAGS.BE_COUPON_CODE); // flag for whether the BE will apply coupon code
  const feCodeEnabled = useFeatureExperiment(FEATURE_FLAGS.FE_COUPON_CODE); // flag for whether the FE will apply coupon code
  const { showMessage } = useGlobalNotification();
  const accessToken = parseJwt(getAccessToken());
  const flagsLoaded = beCodeEnabled !== undefined && feCodeEnabled !== undefined;
  const skipApply = (flagsLoaded && !feCodeEnabled) || !couponCode || accessToken?.role?.code !== SYSTEM_ROLE.TALENT;

  const [applyCoupon, { loading, error, data }] = useMutation(CreateCouponRedemptionDoc, {
    onError: (e) => {
      console.error('error applying coupon', { err: e });

      if (beCodeEnabled) {
        showMessage({
          message: t('common:toast.applyCoupon.failed'),
          color: 'danger',
        });
      }
    },
  });

  const applyComplete =
    flagsLoaded && feCodeEnabled && accessToken?.role?.code === SYSTEM_ROLE.TALENT && Boolean(data || error);

  const redeemCoupon = useCallback(async () => {
    const res = await applyCoupon({ variables: { input: { couponId: couponCode } } });

    // if the BE is disabled we don't want to try and show
    // the toast messages
    if (beCodeEnabled) {
      if (res?.data?.createCouponRedemption) {
        showMessage({
          message: t('common:toast.applyCoupon.success'),
          color: 'success',
        });
      } else {
        showMessage({
          message: t('common:toast.applyCoupon.failed'),
          color: 'danger',
        });
      }
    }
  }, [applyCoupon, beCodeEnabled, couponCode, showMessage, t]);

  useEffect(() => {
    if (
      beCodeEnabled !== undefined &&
      feCodeEnabled !== undefined &&
      feCodeEnabled &&
      couponCode &&
      accessToken?.role?.code === SYSTEM_ROLE.TALENT &&
      !loading &&
      !error &&
      !data
    ) {
      redeemCoupon().catch(() => undefined);
    }
  }, [beCodeEnabled, feCodeEnabled, couponCode, accessToken, loading, error, data, redeemCoupon]);

  useEffect(() => {
    localStorage.removeItem(QUERY_PARAMS.couponCode);
  }, []);

  if (skipApply) {
    return <>{children}</>;
  }

  if (!applyComplete) {
    return <Loader />;
  }

  return <>{children}</>;
};
